import React, { useMemo, useState } from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { FlexboxGrid, ButtonToolbar, Button } from 'rsuite';
import {
  get,
  has,
  map,
  head,
  join,
  omit,
  reduce,
  values,
  isEmpty,
  size,
  flatten
} from 'lodash/fp';
import { trackingMethodOptionValuesToLabels } from '../../../../../redux/Reservation/selectors/tracking-method-options';
import { getOrderedItemsReportSearchQuery } from '../../../../../redux/Reservation/selectors/components/reports/ordered-items';
import { getAll as getOrderedItemsReportResult } from '../../../../../redux/Reservation/selectors/reports/ordered-items';
import { GroupByOptions } from '../../../../../components/reservations/reports/OrderedItemsSearchForm/form-options';
import SearchDetails from '../../../../../components/reservations/reports/SearchDetails';
import ClientExportButton from '../../../../../components/ClientExportCsvButton';
import ReportTemplateFormModal from '../../../../../containers/ReportTemplateFormModal';
import { ORDRED_ITEMS } from '../../../../../constants/reservation/ReportTemplateTypes';
import optionValuesToLabels from '../../../../../helpers/optionValuesToLabels';
import tableColumns from '../../../../../components/reservations/reports/OrderedItemsList/columns';

const reduceWithKey = reduce.convert({ cap: false });

export default function ResultHeader() {
  const [
    showReportTemplateFormModal,
    setShowReportTemplateFormModal
  ] = useState(false);
  const searchQuery = useSelector(getOrderedItemsReportSearchQuery);
  const searchResult = useSelector(getOrderedItemsReportResult);
  const { requestTime, trackingMethodId, groupBy } = searchQuery;

  const searchDetails = [
    { label: 'Reservation Date', value: join(' - ', requestTime) },
    {
      label: 'Tracking Methods',
      value: useSelector(state =>
        trackingMethodOptionValuesToLabels(state, trackingMethodId)
      )
    },
    ...(!isEmpty(groupBy)
      ? [
          {
            label: 'Columns',
            value: optionValuesToLabels(groupBy, GroupByOptions)
          }
        ]
      : [])
  ];

  const resultColumns = useMemo(
    () => {
      if (!size(searchResult)) return [];

      const resultItem = head(searchResult);
      return flatten(
        values(
          reduceWithKey(
            (columns, column, key) =>
              has(key, resultItem) ? { ...columns, [key]: column } : columns,
            {},
            tableColumns
          )
        )
      );
    },
    [tableColumns, searchResult]
  );

  const exportColumns = useMemo(
    () => (size(resultColumns) ? map('label', resultColumns) : []),
    [resultColumns]
  );

  const exportData = useMemo(
    () => {
      if (!size(resultColumns)) return [];
      return map(
        row =>
          reduce(
            (rowData, { dataKey }) => [...rowData, get(dataKey, row)],
            [],
            resultColumns
          ),
        searchResult
      );
    },
    [resultColumns]
  );

  const handleOpenReportTemplateFormModal = () => {
    setShowReportTemplateFormModal(true);
  };

  const handleCloseReportTemplateFormModal = () => {
    setShowReportTemplateFormModal(false);
  };

  return (
    <FlexboxGrid justify="space-between">
      <FlexboxGrid.Item colspan={19}>
        <SearchDetails data={searchDetails} />
      </FlexboxGrid.Item>
      <FlexboxGrid.Item colspan={5}>
        <ButtonToolbar style={{ textAlign: 'right' }}>
          <Button
            appearance="ghost"
            onClick={handleOpenReportTemplateFormModal}
            style={{ display: 'none' }}
          >
            Save Preset
          </Button>
          <ClientExportButton
            filename={`reservation_ordered_items_report_${moment().format(
              'YMMDD'
            )}`}
            headerData={searchDetails}
            columns={exportColumns}
            data={exportData}
          />
        </ButtonToolbar>
        <ReportTemplateFormModal
          fileType={ORDRED_ITEMS}
          file={omit(['requestTime'], searchQuery)}
          show={showReportTemplateFormModal}
          onSubmitted={handleCloseReportTemplateFormModal}
          handleClose={handleCloseReportTemplateFormModal}
        />
      </FlexboxGrid.Item>
    </FlexboxGrid>
  );
}
